import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getPatient,
  getClinicalHistory,
  createClinicalHistory,
  updateClinicalHistory,
  closeClinicalHistory,
  unlockClinicalHistory,
  getSOAPNotes,
  createSOAPNote,
  updateSOAPNote,
  deleteSOAPNote,
  signSOAPNote,
  addSOAPAddendum,
  getAppointments,
} from '../api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../components/ui/sheet';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../utils/apiError';
import {
  ArrowLeft,
  FileText,
  ClipboardList,
  Save,
  Lock,
  Unlock,
  Plus,
  Check,
  Edit3,
  MessageSquarePlus,
  Eye,
  Trash2,
  Calendar,
  Clock,
  DollarSign,
  UserCheck,
  XCircle,
} from 'lucide-react';

function appointmentStartDate(apt) {
  const t = (apt.start_time || '00:00').toString().slice(0, 5);
  return new Date(`${apt.date}T${t}`);
}

const appointmentStatusLabels = {
  PENDIENTE: 'Pendiente',
  CONFIRMADA: 'Confirmada',
  CANCELADA: 'Cancelada',
  NO_SHOW: 'No se presentó',
  COMPLETADA: 'Completada',
  REQUIERE_REAGENDAR: 'Requiere reagendar',
};

function appointmentStatusBadgeClass(status) {
  switch (status) {
    case 'CANCELADA':
      return 'bg-[#9E2A2B] text-white border-transparent hover:bg-[#9E2A2B]';
    case 'COMPLETADA':
      return 'bg-[#335C67] text-white border-transparent hover:bg-[#335C67]';
    case 'CONFIRMADA':
      return 'bg-[#588157] text-white border-transparent hover:bg-[#588157]';
    case 'NO_SHOW':
      return 'bg-[#8D99AE] text-white border-transparent hover:bg-[#8D99AE]';
    case 'REQUIERE_REAGENDAR':
      return 'bg-amber-600 text-white border-transparent hover:bg-amber-600';
    case 'PENDIENTE':
    default:
      return 'bg-[#E09F3E] text-[#1A1A1A] border-transparent hover:bg-[#E09F3E]';
  }
}

/** Una fila de cita en expediente: estado de cita, pago y asistencia muy visibles */
function AppointmentListItem({ apt, variant }) {
  const isCancelled = apt.status === 'CANCELADA';
  const isPaid =
    apt.payment_status === 'pagado' ||
    (Array.isArray(apt.payments) && apt.payments.length > 0);
  const attendance = apt.attendance_status || 'pendiente';

  const dateOpts =
    variant === 'upcoming'
      ? {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        }
      : { day: 'numeric', month: 'short', year: 'numeric' };

  const cardBase =
    variant === 'upcoming'
      ? 'rounded-lg border p-3 bg-white'
      : 'rounded-lg border p-3 bg-[#F8F7F4]';
  const cancelledStyle = isCancelled
    ? ' border-[#9E2A2B] border-2 bg-[#fff5f5] ring-1 ring-[#9E2A2B]/20'
    : ' border-slate-100';

  return (
    <div
      className={`${cardBase}${cancelledStyle}`}
      data-testid={`${variant}-appointment-${apt.id}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="font-medium text-[#2B2D42]">
            {new Date(apt.date).toLocaleDateString('es-MX', dateOpts)}
          </p>
          <p className="text-sm text-[#5D5C61] flex items-center gap-1 mt-1 flex-wrap">
            <Clock className="w-3.5 h-3.5 shrink-0" />
            {(apt.start_time || '').toString().slice(0, 5)} –{' '}
            {(apt.end_time || '').toString().slice(0, 5)}
            {apt.type === 'virtual' && (
              <Badge variant="outline" className="ml-1 text-xs">
                Virtual
              </Badge>
            )}
          </p>
          {apt.reason && (
            <p className="text-sm text-[#5D5C61] mt-2 line-clamp-2">{apt.reason}</p>
          )}
        </div>
        <Badge
          className={`shrink-0 font-semibold inline-flex items-center gap-1 ${appointmentStatusBadgeClass(apt.status)}`}
        >
          {isCancelled && <XCircle className="w-3 h-3" />}
          {appointmentStatusLabels[apt.status] || apt.status}
        </Badge>
      </div>

      <div className="mt-3 pt-3 border-t border-slate-200/80 flex flex-wrap gap-2 items-stretch">
        <div
          className={`flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-semibold ${
            isPaid
              ? 'bg-[#588157] text-white shadow-sm'
              : 'bg-amber-100 text-amber-950 border border-amber-400/80'
          }`}
          title="Estado del pago de la consulta"
        >
          <DollarSign className="w-3.5 h-3.5 shrink-0" />
          {isPaid ? 'Consulta pagada' : 'Sin pagar'}
        </div>

        {isCancelled ? (
          <div
            className="flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-semibold bg-slate-200/80 text-slate-600 border border-dashed border-slate-400"
            title="La cita fue cancelada"
          >
            <UserCheck className="w-3.5 h-3.5 shrink-0 opacity-70" />
            Asistencia no aplica
          </div>
        ) : (
          <div
            className={`flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-semibold ${
              attendance === 'asistio'
                ? 'bg-emerald-100 text-emerald-950 border border-emerald-500/70'
                : attendance === 'no_asistio'
                  ? 'bg-rose-100 text-rose-950 border border-rose-400/80'
                  : 'bg-slate-100 text-slate-700 border border-slate-300'
            }`}
            title="Registro de asistencia a la cita"
          >
            <UserCheck className="w-3.5 h-3.5 shrink-0" />
            {attendance === 'asistio'
              ? 'Asistió'
              : attendance === 'no_asistio'
                ? 'No asistió'
                : 'Asistencia pendiente'}
          </div>
        )}
      </div>
    </div>
  );
}

export default function ClinicalRecord() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  
  const [patient, setPatient] = useState(null);
  const [history, setHistory] = useState(null);
  const [historyExists, setHistoryExists] = useState(false);
  const [soapNotes, setSOAPNotes] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [selectedNote, setSelectedNote] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('history');
  
  const [historyForm, setHistoryForm] = useState({
    chief_complaint: '',
    history_present_illness: '',
    past_psychiatric_history: '',
    past_medical_history: '',
    family_history: '',
    social_history: '',
    medications: '',
    allergies: '',
    mental_status_exam: '',
    diagnosis: '',
    treatment_plan: '',
  });
  
  const [soapForm, setSOAPForm] = useState({
    subjective: '',
    objective: '',
    assessment: '',
    plan: '',
  });
  
  const [noteDrawerOpen, setNoteDrawerOpen] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [unlockDialogOpen, setUnlockDialogOpen] = useState(false);
  const [unlockReason, setUnlockReason] = useState('');
  const [addendumDialogOpen, setAddendumDialogOpen] = useState(false);
  const [addendumContent, setAddendumContent] = useState('');
  const [addendumNoteId, setAddendumNoteId] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const patientRes = await getPatient(patientId);
      // Laravel devuelve {data: {...}} para item único
      const patientData = patientRes.data?.data || patientRes.data;
      setPatient(patientData);
      
      try {
        const historyRes = await getClinicalHistory(patientId);
        const historyData = historyRes.data?.data || historyRes.data;
        setHistory(historyData);
        setHistoryExists(true);
        setHistoryForm({
          chief_complaint: historyData.chief_complaint || '',
          history_present_illness: historyData.history_present_illness || '',
          past_psychiatric_history: historyData.past_psychiatric_history || '',
          past_medical_history: historyData.past_medical_history || '',
          family_history: historyData.family_history || '',
          social_history: historyData.social_history || '',
          medications: historyData.medications || '',
          allergies: historyData.allergies || '',
          mental_status_exam: historyData.mental_status_exam || '',
          diagnosis: historyData.diagnosis || '',
          treatment_plan: historyData.treatment_plan || '',
        });
      } catch (error) {
        if (error.response?.status === 404) {
          setHistoryExists(false);
        }
      }
      
      const notesRes = await getSOAPNotes(patientId);
      // Laravel devuelve {data: [...]} para listas
      setSOAPNotes(Array.isArray(notesRes.data?.data) ? notesRes.data.data : (Array.isArray(notesRes.data) ? notesRes.data : []));

      try {
        const aptsRes = await getAppointments({ patient_id: patientId });
        const list = Array.isArray(aptsRes.data?.data)
          ? aptsRes.data.data
          : Array.isArray(aptsRes.data)
            ? aptsRes.data
            : [];
        setAppointments(list);
      } catch {
        setAppointments([]);
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error al cargar expediente'));
    }
  }, [patientId]);

  const { upcomingAppointments, pastAppointments } = useMemo(() => {
    const now = new Date();
    const upcoming = [];
    const past = [];
    for (const apt of appointments) {
      const start = appointmentStartDate(apt);
      if (start >= now) upcoming.push(apt);
      else past.push(apt);
    }
    upcoming.sort((a, b) => appointmentStartDate(a) - appointmentStartDate(b));
    past.sort((a, b) => appointmentStartDate(b) - appointmentStartDate(a));
    return { upcomingAppointments: upcoming, pastAppointments: past };
  }, [appointments]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSaveHistory = async () => {
    setIsLoading(true);
    try {
      if (historyExists) {
        await updateClinicalHistory(patientId, historyForm);
        toast.success('Historia clínica actualizada');
      } else {
        await createClinicalHistory(patientId, historyForm);
        setHistoryExists(true);
        toast.success('Historia clínica creada');
      }
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error al guardar'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseHistory = async () => {
    if (!window.confirm('¿Cerrar historia clínica? Esta acción requiere permiso de administrador para revertirse.')) return;
    try {
      await closeClinicalHistory(patientId);
      toast.success('Historia clínica cerrada');
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error al cerrar'));
    }
  };

  const handleUnlockHistory = async () => {
    if (unlockReason.length < 10) {
      toast.error('El motivo debe tener al menos 10 caracteres');
      return;
    }
    try {
      await unlockClinicalHistory(patientId, unlockReason);
      toast.success('Historia clínica desbloqueada');
      setUnlockDialogOpen(false);
      setUnlockReason('');
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error al desbloquear'));
    }
  };

  const handleOpenNoteDrawer = (note = null) => {
    setEditingNote(note);
    if (note) {
      setSOAPForm({
        subjective: note.subjective || '',
        objective: note.objective || '',
        assessment: note.assessment || '',
        plan: note.plan || '',
      });
    } else {
      setSOAPForm({
        subjective: '',
        objective: '',
        assessment: '',
        plan: '',
      });
    }
    setNoteDrawerOpen(true);
  };

  const handleSaveSOAPNote = async () => {
    setIsLoading(true);
    try {
      if (editingNote) {
        await updateSOAPNote(editingNote.id, soapForm);
        toast.success('Nota SOAP actualizada');
      } else {
        await createSOAPNote(patientId, soapForm);
        toast.success('Nota SOAP creada');
      }
      setNoteDrawerOpen(false);
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error al guardar'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignNote = async (noteId) => {
    if (!window.confirm('¿Firmar esta nota? No podrá editarse después.')) return;
    try {
      await signSOAPNote(noteId);
      toast.success('Nota SOAP firmada');
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error al firmar'));
    }
  };

  const handleAddAddendum = async () => {
    if (addendumContent.length < 5) {
      toast.error('El addendum debe tener contenido');
      return;
    }
    try {
      await addSOAPAddendum(addendumNoteId, addendumContent);
      toast.success('Addendum agregado');
      setAddendumDialogOpen(false);
      setAddendumContent('');
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error al agregar addendum'));
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('¿Eliminar esta nota SOAP? Solo se pueden eliminar notas en borrador.')) return;
    try {
      await deleteSOAPNote(noteId);
      toast.success('Nota SOAP eliminada');
      setNoteDrawerOpen(false);
      setEditingNote(null);
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error al eliminar'));
    }
  };

  const isHistoryClosed = history?.status === 'CERRADA';

  const historyFields = [
    { key: 'chief_complaint', label: 'Motivo de consulta' },
    { key: 'history_present_illness', label: 'Historia de la enfermedad actual' },
    { key: 'past_psychiatric_history', label: 'Antecedentes psiquiátricos' },
    { key: 'past_medical_history', label: 'Antecedentes médicos' },
    { key: 'family_history', label: 'Antecedentes familiares' },
    { key: 'social_history', label: 'Historia social' },
    { key: 'medications', label: 'Medicamentos actuales' },
    { key: 'allergies', label: 'Alergias' },
    { key: 'mental_status_exam', label: 'Examen del estado mental' },
    { key: 'diagnosis', label: 'Diagnóstico' },
    { key: 'treatment_plan', label: 'Plan de tratamiento' },
  ];

  if (!patient) {
    return (
      <div className="p-6 md:p-10">
        <p>Cargando...</p>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-10">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/expedientes')}
          className="hover:bg-[#F4F1EA]"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-[#2B2D42] font-['Manrope']">
            {patient.full_name || patient.name}
          </h1>
          <p className="text-[#5D5C61] mt-1">Expediente clínico</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={patient.otp_verified ? 'default' : 'secondary'}>
            {patient.otp_verified ? 'Verificado' : 'Sin verificar'}
          </Badge>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-[#F4F1EA]">
          <TabsTrigger
            value="history"
            className="data-[state=active]:bg-[#335C67] data-[state=active]:text-white"
            data-testid="tab-history"
          >
            <FileText className="w-4 h-4 mr-2" />
            Historia Clínica
          </TabsTrigger>
          <TabsTrigger
            value="appointments"
            className="data-[state=active]:bg-[#335C67] data-[state=active]:text-white"
            data-testid="tab-appointments"
          >
            <Calendar className="w-4 h-4 mr-2" />
            Citas ({appointments.length})
          </TabsTrigger>
          <TabsTrigger
            value="soap"
            className="data-[state=active]:bg-[#335C67] data-[state=active]:text-white"
            data-testid="tab-soap"
          >
            <ClipboardList className="w-4 h-4 mr-2" />
            Notas SOAP ({soapNotes.length})
          </TabsTrigger>
        </TabsList>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-6">
          <Card className="border-slate-100" data-testid="clinical-history-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-xl font-['Manrope']">Historia Clínica</CardTitle>
                <CardDescription>
                  {isHistoryClosed
                    ? 'Historia cerrada - Solo lectura'
                    : historyExists
                    ? 'Última actualización: ' + new Date(history?.updated_at).toLocaleDateString('es-MX')
                    : 'Nueva historia clínica'}
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                {isHistoryClosed ? (
                  <>
                    <Badge className="bg-[#9E2A2B]">
                      <Lock className="w-3 h-3 mr-1" />
                      Cerrada
                    </Badge>
                    {isAdmin && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setUnlockDialogOpen(true)}
                        data-testid="unlock-history-btn"
                      >
                        <Unlock className="w-4 h-4 mr-1" />
                        Desbloquear
                      </Button>
                    )}
                  </>
                ) : (
                  historyExists && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCloseHistory}
                      data-testid="close-history-btn"
                    >
                      <Lock className="w-4 h-4 mr-1" />
                      Cerrar historia
                    </Button>
                  )
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid gap-6">
                {historyFields.map((field) => (
                  <div key={field.key} className="space-y-2">
                    <Label className="font-medium text-[#2B2D42]">{field.label}</Label>
                    <Textarea
                      value={historyForm[field.key]}
                      onChange={(e) =>
                        setHistoryForm({ ...historyForm, [field.key]: e.target.value })
                      }
                      placeholder={`Ingrese ${field.label.toLowerCase()}...`}
                      rows={3}
                      disabled={isHistoryClosed}
                      className="bg-white disabled:bg-[#F4F1EA] disabled:cursor-not-allowed"
                      data-testid={`history-${field.key}`}
                    />
                  </div>
                ))}
              </div>
              
              {!isHistoryClosed && (
                <div className="mt-6 pt-6 border-t border-slate-100">
                  <Button
                    className="bg-[#335C67] hover:bg-[#244048] text-white rounded-full"
                    onClick={handleSaveHistory}
                    disabled={isLoading}
                    data-testid="save-history-btn"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {isLoading ? 'Guardando...' : 'Guardar Historia Clínica'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Citas */}
        <TabsContent value="appointments" className="space-y-6">
          <div className="grid gap-6 lg:grid-cols-2">
            <Card className="border-slate-100" data-testid="upcoming-appointments-card">
              <CardHeader>
                <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-[#335C67]" />
                  Próximas citas
                </CardTitle>
                <CardDescription>
                  Citas con fecha y hora de inicio a partir de ahora (incluye canceladas futuras).
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {upcomingAppointments.length === 0 ? (
                  <p className="text-sm text-[#5D5C61]">No hay citas próximas programadas.</p>
                ) : (
                  upcomingAppointments.map((apt) => (
                    <AppointmentListItem key={apt.id} apt={apt} variant="upcoming" />
                  ))
                )}
              </CardContent>
            </Card>

            <Card className="border-slate-100" data-testid="past-appointments-card">
              <CardHeader>
                <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
                  <Clock className="w-5 h-5 text-[#8D99AE]" />
                  Citas anteriores
                </CardTitle>
                <CardDescription>
                  Historial de citas pasadas, incluidas canceladas.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 max-h-[min(70vh,520px)] overflow-y-auto">
                {pastAppointments.length === 0 ? (
                  <p className="text-sm text-[#5D5C61]">No hay citas anteriores registradas.</p>
                ) : (
                  pastAppointments.map((apt) => (
                    <AppointmentListItem key={apt.id} apt={apt} variant="past" />
                  ))
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* SOAP Notes Tab */}
        <TabsContent value="soap" className="space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-semibold text-[#2B2D42] font-['Manrope']">
                Notas de Seguimiento SOAP
              </h2>
              <p className="text-sm text-[#5D5C61]">
                Seguimiento periódico del paciente
              </p>
            </div>
            <Button
              className="bg-[#335C67] hover:bg-[#244048] text-white rounded-full"
              onClick={() => handleOpenNoteDrawer()}
              data-testid="new-soap-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nueva Nota SOAP
            </Button>
          </div>

          <div className="grid gap-4">
            {soapNotes.map((note, index) => (
              <Card
                key={note.id}
                className="border-slate-100 hover:shadow-md transition-all"
                data-testid={`soap-note-${note.id}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#335C67]/10 flex items-center justify-center">
                        <ClipboardList className="w-5 h-5 text-[#335C67]" />
                      </div>
                      <div>
                        <CardTitle className="text-base">
                          Nota #{note.number ?? soapNotes.length - index}
                        </CardTitle>
                        <CardDescription>
                          {new Date(note.created_at).toLocaleDateString('es-MX', {
                            day: '2-digit',
                            month: 'long',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge
                        className={
                          note.status === 'FIRMADA'
                            ? 'bg-[#588157] hover:bg-[#588157]'
                            : 'bg-[#E09F3E] hover:bg-[#E09F3E]'
                        }
                      >
                        {note.status === 'FIRMADA' ? (
                          <>
                            <Check className="w-3 h-3 mr-1" />
                            Firmada
                          </>
                        ) : (
                          'Borrador'
                        )}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="font-semibold text-[#335C67]">S</span>
                      <span className="text-[#8D99AE] ml-1">Subjetivo</span>
                      <p className="text-[#2B2D42] mt-1 line-clamp-2">
                        {note.subjective || '-'}
                      </p>
                    </div>
                    <div>
                      <span className="font-semibold text-[#335C67]">O</span>
                      <span className="text-[#8D99AE] ml-1">Objetivo</span>
                      <p className="text-[#2B2D42] mt-1 line-clamp-2">
                        {note.objective || '-'}
                      </p>
                    </div>
                    <div>
                      <span className="font-semibold text-[#335C67]">A</span>
                      <span className="text-[#8D99AE] ml-1">Evaluación</span>
                      <p className="text-[#2B2D42] mt-1 line-clamp-2">
                        {note.assessment || '-'}
                      </p>
                    </div>
                    <div>
                      <span className="font-semibold text-[#335C67]">P</span>
                      <span className="text-[#8D99AE] ml-1">Plan</span>
                      <p className="text-[#2B2D42] mt-1 line-clamp-2">
                        {note.plan || '-'}
                      </p>
                    </div>
                  </div>

                  {/* Addendums */}
                  {note.addendums && note.addendums.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-slate-100">
                      <p className="text-xs font-semibold text-[#8D99AE] uppercase tracking-wider mb-2">
                        Addendums
                      </p>
                      {note.addendums.map((addendum) => (
                        <div
                          key={addendum.id}
                          className="bg-[#F4F1EA] rounded-lg p-3 text-sm mb-2"
                        >
                          <p className="text-[#2B2D42]">{addendum.content}</p>
                          <p className="text-xs text-[#8D99AE] mt-1">
                            {new Date(addendum.created_at).toLocaleDateString('es-MX')}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="flex gap-2 mt-4 pt-4 border-t border-slate-100">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedNote(note)}
                      data-testid={`view-soap-${note.id}`}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Ver completa
                    </Button>
                    {note.status === 'BORRADOR' && (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenNoteDrawer(note)}
                          data-testid={`edit-soap-${note.id}`}
                        >
                          <Edit3 className="w-4 h-4 mr-1" />
                          Editar
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-[#588157]"
                          onClick={() => handleSignNote(note.id)}
                          data-testid={`sign-soap-${note.id}`}
                        >
                          <Check className="w-4 h-4 mr-1" />
                          Firmar
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-[#9E2A2B]"
                          onClick={() => handleDeleteNote(note.id)}
                          data-testid={`delete-soap-${note.id}`}
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Eliminar
                        </Button>
                      </>
                    )}
                    {note.status === 'FIRMADA' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setAddendumNoteId(note.id);
                          setAddendumDialogOpen(true);
                        }}
                        data-testid={`addendum-soap-${note.id}`}
                      >
                        <MessageSquarePlus className="w-4 h-4 mr-1" />
                        Agregar addendum
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}

            {soapNotes.length === 0 && (
              <div className="text-center py-12">
                <ClipboardList className="w-12 h-12 text-[#8D99AE] mx-auto mb-4" />
                <h3 className="text-lg font-medium text-[#2B2D42]">
                  No hay notas SOAP
                </h3>
                <p className="text-[#5D5C61] mt-1">
                  Crea la primera nota de seguimiento
                </p>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* SOAP Note Drawer */}
      <Sheet open={noteDrawerOpen} onOpenChange={setNoteDrawerOpen}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="soap-drawer">
          <SheetHeader>
            <SheetTitle className="text-2xl font-['Manrope']">
              {editingNote ? 'Editar Nota SOAP' : 'Nueva Nota SOAP'}
            </SheetTitle>
            <SheetDescription>
              {editingNote
                ? 'Modifica la nota de seguimiento'
                : 'Registra una nueva nota de seguimiento'}
            </SheetDescription>
          </SheetHeader>

          {/* Previous note reference */}
          {!editingNote && soapNotes.length > 0 && (
            <div className="mt-4 p-4 bg-[#F4F1EA] rounded-lg">
              <p className="text-xs font-semibold text-[#8D99AE] uppercase tracking-wider mb-2">
                Nota anterior (referencia)
              </p>
              <div className="text-sm text-[#5D5C61] space-y-1">
                <p>
                  <strong>S:</strong> {soapNotes[0].subjective?.slice(0, 100) || '-'}...
                </p>
                <p>
                  <strong>A:</strong> {soapNotes[0].assessment?.slice(0, 100) || '-'}...
                </p>
              </div>
            </div>
          )}

          <div className="mt-6 space-y-5">
            <div className="space-y-2">
              <Label className="font-medium text-[#2B2D42] flex items-center gap-2">
                <span className="w-6 h-6 bg-[#335C67] text-white rounded flex items-center justify-center text-sm font-bold">
                  S
                </span>
                Subjetivo
              </Label>
              <Textarea
                value={soapForm.subjective}
                onChange={(e) => setSOAPForm({ ...soapForm, subjective: e.target.value })}
                placeholder="Lo que el paciente refiere, síntomas, quejas..."
                rows={4}
                className="bg-white font-['Public_Sans']"
                data-testid="soap-subjective"
              />
            </div>

            <div className="space-y-2">
              <Label className="font-medium text-[#2B2D42] flex items-center gap-2">
                <span className="w-6 h-6 bg-[#335C67] text-white rounded flex items-center justify-center text-sm font-bold">
                  O
                </span>
                Objetivo
              </Label>
              <Textarea
                value={soapForm.objective}
                onChange={(e) => setSOAPForm({ ...soapForm, objective: e.target.value })}
                placeholder="Observaciones clínicas, signos vitales, exámenes..."
                rows={4}
                className="bg-white font-['Public_Sans']"
                data-testid="soap-objective"
              />
            </div>

            <div className="space-y-2">
              <Label className="font-medium text-[#2B2D42] flex items-center gap-2">
                <span className="w-6 h-6 bg-[#335C67] text-white rounded flex items-center justify-center text-sm font-bold">
                  A
                </span>
                Evaluación (Assessment)
              </Label>
              <Textarea
                value={soapForm.assessment}
                onChange={(e) => setSOAPForm({ ...soapForm, assessment: e.target.value })}
                placeholder="Diagnóstico, interpretación clínica..."
                rows={4}
                className="bg-white font-['Public_Sans']"
                data-testid="soap-assessment"
              />
            </div>

            <div className="space-y-2">
              <Label className="font-medium text-[#2B2D42] flex items-center gap-2">
                <span className="w-6 h-6 bg-[#335C67] text-white rounded flex items-center justify-center text-sm font-bold">
                  P
                </span>
                Plan
              </Label>
              <Textarea
                value={soapForm.plan}
                onChange={(e) => setSOAPForm({ ...soapForm, plan: e.target.value })}
                placeholder="Plan de tratamiento, medicamentos, indicaciones..."
                rows={4}
                className="bg-white font-['Public_Sans']"
                data-testid="soap-plan"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                className="flex-1 bg-[#335C67] hover:bg-[#244048] text-white rounded-full"
                onClick={handleSaveSOAPNote}
                disabled={isLoading}
                data-testid="save-soap-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {isLoading ? 'Guardando...' : 'Guardar Nota'}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* View Note Dialog */}
      <Dialog open={!!selectedNote} onOpenChange={() => setSelectedNote(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">
              Nota SOAP - {selectedNote && new Date(selectedNote.created_at).toLocaleDateString('es-MX')}
            </DialogTitle>
          </DialogHeader>
          {selectedNote && (
            <div className="space-y-6">
              <div className="space-y-2">
                <Label className="font-semibold text-[#335C67]">S - Subjetivo</Label>
                <p className="text-[#2B2D42] whitespace-pre-wrap bg-[#F4F1EA] p-4 rounded-lg">
                  {selectedNote.subjective || '-'}
                </p>
              </div>
              <div className="space-y-2">
                <Label className="font-semibold text-[#335C67]">O - Objetivo</Label>
                <p className="text-[#2B2D42] whitespace-pre-wrap bg-[#F4F1EA] p-4 rounded-lg">
                  {selectedNote.objective || '-'}
                </p>
              </div>
              <div className="space-y-2">
                <Label className="font-semibold text-[#335C67]">A - Evaluación</Label>
                <p className="text-[#2B2D42] whitespace-pre-wrap bg-[#F4F1EA] p-4 rounded-lg">
                  {selectedNote.assessment || '-'}
                </p>
              </div>
              <div className="space-y-2">
                <Label className="font-semibold text-[#335C67]">P - Plan</Label>
                <p className="text-[#2B2D42] whitespace-pre-wrap bg-[#F4F1EA] p-4 rounded-lg">
                  {selectedNote.plan || '-'}
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Unlock Dialog */}
      <Dialog open={unlockDialogOpen} onOpenChange={setUnlockDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Desbloquear Historia Clínica</DialogTitle>
            <DialogDescription>
              Esta acción quedará registrada en el log de auditoría. Ingresa el motivo del desbloqueo.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Textarea
              value={unlockReason}
              onChange={(e) => setUnlockReason(e.target.value)}
              placeholder="Motivo del desbloqueo (mínimo 10 caracteres)..."
              rows={3}
              data-testid="unlock-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUnlockDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              className="bg-[#335C67] hover:bg-[#244048]"
              onClick={handleUnlockHistory}
              data-testid="confirm-unlock-btn"
            >
              Desbloquear
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Addendum Dialog */}
      <Dialog open={addendumDialogOpen} onOpenChange={setAddendumDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Agregar Addendum</DialogTitle>
            <DialogDescription>
              Agrega información adicional a la nota firmada. Esta acción quedará registrada.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Textarea
              value={addendumContent}
              onChange={(e) => setAddendumContent(e.target.value)}
              placeholder="Contenido del addendum..."
              rows={4}
              data-testid="addendum-content-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddendumDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              className="bg-[#335C67] hover:bg-[#244048]"
              onClick={handleAddAddendum}
              data-testid="confirm-addendum-btn"
            >
              Agregar Addendum
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
